import { createReference, getReferenceString } from '@medplum/core';
import { useCallback, useState } from 'react';
import { fetchBot } from '@/services/services';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Encounter, Practitioner, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { alert } from '@/services/alert';
import { collectQuestionnaireResponseUpdateFieldErrors } from '../services/questionnaireResponseUpdateErrors';
import { trimQuestionnaireResponseForSave } from '../services/trimQuestionnaireResponseForSave';
import { persistEncounterQuestionnaireResponses } from '../services/persistEncounterQuestionnaireResponses';
import { useTimelineContext } from './useTimelineContext';

export function useSignEncounter(
  handleNavigateToItem: ((itemReference: string) => void) | undefined,
  handleSignEncounterSuccess?: () => void
) {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { timelineContext } = useTimelineContext();
  const [isSigning, setIsSigning] = useState(false);
  const [isSigningSuccess, setIsSigningSuccess] = useState(false);

  const handleSignEncounter = useCallback(
    async (encounter: Encounter, pendingResponses: Record<string, QuestionnaireResponse> = {}, tasks: Task[] = []) => {
      setIsSigning(true);
      setIsSigningSuccess(false);
      try {
        const entries = Object.entries(pendingResponses);
        const tasksById = new Map<string, Task>();
        for (const task of tasks) {
          if (task.id) {
            tasksById.set(task.id, task);
          }
        }

        const pendingResponseList = entries.map(([, response]) => trimQuestionnaireResponseForSave(response));
        if (pendingResponseList.length) {
          const results = await persistEncounterQuestionnaireResponses(
            medplum,
            entries.map(([taskId, response]) => ({ taskId, response })),
            tasksById
          );

          const hasRejected = results.some((r) => r.status === 'rejected');
          const fieldErrors = collectQuestionnaireResponseUpdateFieldErrors(pendingResponseList, results);

          if (hasRejected) {
            if (fieldErrors.length) {
              alert.error(`Couldn't sign chart. Some fields could not be processed:\n- ${fieldErrors.join('\n- ')}`);
            } else {
              const firstReason = results.find((r) => r.status === 'rejected')?.reason;
              alert.error(
                `Couldn't sign chart: ${
                  firstReason instanceof Error ? firstReason.message : 'Unknown error updating questionnaire response'
                }`
              );
            }
            setIsSigningSuccess(false);
            return;
          }
        }

        await fetchBot(medplum, 'sign-encounter', {
          encounter: createReference(encounter),
          patient: encounter.subject!,
          practitioner: createReference(profile!),
        });

        timelineContext.refresh();

        if (encounter && handleNavigateToItem) {
          const encounterReference = createReference(encounter);
          const referenceString = getReferenceString(encounterReference);
          if (referenceString) {
            handleNavigateToItem(referenceString);
          }
        }

        setIsSigningSuccess(true);
        handleSignEncounterSuccess?.();
      } catch (error) {
        alert.error(`Couldn't sign chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setIsSigningSuccess(false);
      } finally {
        setIsSigning(false);
      }
    },
    [medplum, profile, timelineContext, handleNavigateToItem, handleSignEncounterSuccess]
  );

  return {
    isSigning,
    isSigningSuccess,
    handleSignEncounter,
  };
}
